/**
 * Single source of truth for the User Permissions screen.
 *
 * The 15 OS-permission columns (order mirrors the legacy matrix), plus small
 * pure helpers shared by the matrix, multi-select, grant-rate panels, chart,
 * and CSV export. Keys match the user_permissions column names / the backend
 * VALID_PERMISSIONS whitelist.
 */

export const PERMISSIONS = [
  { key: 'notifications_watch', label: 'Notification (Watch)', platform: 'watch' },
  { key: 'reminders_watch', label: 'Reminders (Watch)', platform: 'watch' },
  { key: 'activity_recognition_watch', label: 'Activity recognition (Watch)', platform: 'watch' },
  { key: 'body_sensors_watch', label: 'Body sensors (Watch)', platform: 'watch' },
  { key: 'body_sensors_in_background_watch', label: 'Body sensors · background (Watch)', platform: 'watch' },
  { key: 'heart_rate_watch', label: 'Heart rate (Watch)', platform: 'watch' },
  { key: 'steps_watch', label: 'Steps (Watch)', platform: 'watch' },
  { key: 'workout_watch', label: 'Workout (Watch)', platform: 'watch' },
  { key: 'location_mobile', label: 'Location (Mobile)', platform: 'mobile' },
  { key: 'location_when_in_use_mobile', label: 'Location · in use (Mobile)', platform: 'mobile' },
  { key: 'location_always_mobile', label: 'Location · always (Mobile)', platform: 'mobile' },
  { key: 'calendar_full_access_mobile', label: 'Calendar full access (Mobile)', platform: 'mobile' },
  { key: 'notifications_mobile', label: 'Notifications (Mobile)', platform: 'mobile' },
  { key: 'reminders_mobile', label: 'Reminders (Mobile)', platform: 'mobile' },
  { key: 'app_usage_mobile', label: 'App usage (Mobile)', platform: 'mobile' },
];

// Default "key permissions" surfaced in the summary panels/chart before the
// admin narrows the multi-select (the health-critical grants).
export const DEFAULT_KEY_PERMISSIONS = [
  'notifications_watch', 'heart_rate_watch', 'body_sensors_watch',
  'steps_watch', 'location_always_mobile', 'notifications_mobile',
];

// A grant reaches the page in either of two representations: numeric 0/1 (older
// app builds) or boolean true/false (newer ones) — and, because the permission
// columns are `character varying` in some environments, either can arrive as a
// string ('1', 'true', 'TRUE'). Both read the same way here.
// Anything else — null, undefined, empty string — is "not applicable", never denied.
const GRANTED_VALUES = ['1', 'true', 't', 'yes', 'y'];
const DENIED_VALUES = ['0', 'false', 'f', 'no', 'n'];

export const chipState = (value) => {
  if (value === null || value === undefined) return 'na';
  const v = String(value).trim().toLowerCase();
  if (GRANTED_VALUES.includes(v)) return 'granted';
  if (DENIED_VALUES.includes(v)) return 'denied';
  return 'na';
};

// Rounded grant-rate percentage; divide-by-zero → 0 (never NaN).
export const grantRate = (count, total) => {
  const c = Number(count) || 0;
  const t = Number(total) || 0;
  return t > 0 ? Math.round((c / t) * 100) : 0;
};

// {key:true} selection map → csv of selected keys (for the `permissions` param).
export const permissionsParam = (selectedMap = {}) =>
  Object.entries(selectedMap)
    .filter(([, on]) => on)
    .map(([k]) => k)
    .join(',');
